import { useState } from "react";

import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import styled from 'styled-components';

import { Nullable } from "../../../../services/object";
import ConfirmationModal from "../../ConfirmationModal";

import Rows from "../../../atoms/Rows";
import ActionText from "../../../molecules/ActionText";
import Body from "../../../molecules/Body";
import Button from "../../../molecules/Button";
import CTAButton from "../../../molecules/CTAButton";
import Header from "../../../molecules/Header";

const TextWrapper = styled.div`
  &  * {
    display: inline-block;
  }
`

type Props = Readonly<{
  error: Nullable<Error>;
  logoutRoute: string;
}>

const DisplayAuthError = ({ error, logoutRoute }: Props) => {
  const { t } = useTranslation();

  const [shouldNavigate, setShouldNavigate] = useState(false);

  const reload = () => window.location.reload();
  const logout = () => setShouldNavigate(true)

  return shouldNavigate ? <Navigate to={logoutRoute}/> : (
    <ConfirmationModal
      header={<Header as="h4">{t('An authentication error has occurred')}</Header>}
      body={(
        <Rows gap={100}>
          <TextWrapper>
            <Body size={300}>
              {t('You can either log out or try to reload the page. You may also let us know about it at')}{' '}
              <ActionText
                as="a"
                href={logoutRoute}
                target="_blank"
                rel="noopener noreferrer"
                size={300}
              >
                #nfs-feedback
              </ActionText>
            </Body>
          </TextWrapper>
          <Body size={200} variant="secondary">{t('Error: {{error}}, {{errorMessage}}', {
            error: error?.name ?? t('Unknown auth error'),
            errorMessage: error?.message ?? t('No message')
          })}</Body>
        </Rows>
      )}
      onConfirm={logout}
      confirmButton={(
        <CTAButton
          onClick={logout}
          ref={(node) => node?.focus()}
        >
          {t('Log out')}
        </CTAButton>
      )}
      onDeny={reload}
      closeButton={
        <Button onClick={reload} variant="secondary">
          {t('Reload')}
        </Button>
      }
    />
  )
}

export default DisplayAuthError;
